import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, useFormikContext, getIn } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";

// --- Data for Dropdowns ---
// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     { value: "aws", label: "Amazon Web Services" },
//     { value: "azure", label: "Microsoft Azure" },
// ];
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

const providerRegionToZoneMap: { [provider: string]: { [region: string]: string[] } } = {
    gcp: {
        "asia-east1": ["asia-east1-a", "asia-east1-b", "asia-east1-c"],
        "asia-east2": ["asia-east2-a", "asia-east2-b", "asia-east2-c"],
        "asia-northeast1": ["asia-northeast1-a", "asia-northeast1-b", "asia-northeast1-c"],
        "asia-northeast2": ["asia-northeast2-a", "asia-northeast2-b", "asia-northeast2-c"],
        "asia-northeast3": ["asia-northeast3-a", "asia-northeast3-b", "asia-northeast3-c"],
        "asia-south1": ["asia-south1-a", "asia-south1-b", "asia-south1-c"],
        "asia-south2": ["asia-south2-a", "asia-south2-b", "asia-south2-c"],
        "asia-southeast1": ["asia-southeast1-a", "asia-southeast1-b", "asia-southeast1-c"],
        "asia-southeast2": ["asia-southeast2-a", "asia-southeast2-b", "asia-southeast2-c"],
        "australia-southeast1": ["australia-southeast1-a", "australia-southeast1-b", "australia-southeast1-c"],
        "australia-southeast2": ["australia-southeast2-a", "australia-southeast2-b", "australia-southeast2-c"],
        "europe-central2": ["europe-central2-a", "europe-central2-b", "europe-central2-c"],
        "europe-north1": ["europe-north1-a", "europe-north1-b", "europe-north1-c"],
        "europe-southwest1": ["europe-southwest1-a", "europe-southwest1-b", "europe-southwest1-c"],
        "europe-west1": ["europe-west1-b", "europe-west1-c", "europe-west1-d"],
        "europe-west2": ["europe-west2-a", "europe-west2-b", "europe-west2-c"],
        "europe-west3": ["europe-west3-a", "europe-west3-b", "europe-west3-c"],
        "europe-west4": ["europe-west4-a", "europe-west4-b", "europe-west4-c"],
        "europe-west6": ["europe-west6-a", "europe-west6-b", "europe-west6-c"],
        "europe-west8": ["europe-west8-a", "europe-west8-b", "europe-west8-c"],
        "europe-west9": ["europe-west9-a", "europe-west9-b", "europe-west9-c"],
        "me-central1": ["me-central1-a", "me-central1-b", "me-central1-c"],
        "me-west1": ["me-west1-a", "me-west1-b", "me-west1-c"],
        "northamerica-northeast1": ["northamerica-northeast1-a", "northamerica-northeast1-b", "northamerica-northeast1-c"],
        "northamerica-northeast2": ["northamerica-northeast2-a", "northamerica-northeast2-b", "northamerica-northeast2-c"],
        "southamerica-east1": ["southamerica-east1-a", "southamerica-east1-b", "southamerica-east1-c"],
        "southamerica-west1": ["southamerica-west1-a", "southamerica-west1-b", "southamerica-west1-c"],
        "us-central1": ["us-central1-a", "us-central1-b", "us-central1-c", "us-central1-f"],
        "us-east1": ["us-east1-b", "us-east1-c", "us-east1-d"],
        "us-east4": ["us-east4-a", "us-east4-b", "us-east4-c"],
        "us-east5": ["us-east5-a", "us-east5-b", "us-east5-c"],
        "us-south1": ["us-south1-a", "us-south1-b", "us-south1-c"],
        "us-west1": ["us-west1-a", "us-west1-b", "us-west1-c"],
        "us-west2": ["us-west2-a", "us-west2-b", "us-west2-c"],
        "us-west3": ["us-west3-a", "us-west3-b", "us-west3-c"],
        "us-west4": ["us-west4-a", "us-west4-b", "us-west4-c"],
    },
    aws: {
        "us-east-1": ["us-east-1a", "us-east-1b", "us-east-1c", "us-east-1d", "us-east-1e", "us-east-1f"],
        "us-east-2": ["us-east-2a", "us-east-2b", "us-east-2c"],
        "us-west-1": ["us-west-1a", "us-west-1c"],
        "us-west-2": ["us-west-2a", "us-west-2b", "us-west-2c", "us-west-2d"],
        "af-south-1": ["af-south-1a", "af-south-1b", "af-south-1c"],
        "ap-east-1": ["ap-east-1a", "ap-east-1b", "ap-east-1c"],
        "ap-south-1": ["ap-south-1a", "ap-south-1b", "ap-south-1c"],
        "ap-south-2": ["ap-south-2a", "ap-south-2b", "ap-south-2c"],
        "ap-northeast-1": ["ap-northeast-1a", "ap-northeast-1c", "ap-northeast-1d"],
        "ap-northeast-2": ["ap-northeast-2a", "ap-northeast-2b", "ap-northeast-2c", "ap-northeast-2d"],
        "ap-northeast-3": ["ap-northeast-3a", "ap-northeast-3b", "ap-northeast-3c"],
        "ap-southeast-1": ["ap-southeast-1a", "ap-southeast-1b", "ap-southeast-1c"],
        "ap-southeast-2": ["ap-southeast-2a", "ap-southeast-2b", "ap-southeast-2c"],
        "ap-southeast-3": ["ap-southeast-3a", "ap-southeast-3b", "ap-southeast-3c"],
        "ap-southeast-4": ["ap-southeast-4a", "ap-southeast-4b", "ap-southeast-4c"],
        "ca-central-1": ["ca-central-1a", "ca-central-1b", "ca-central-1d"],
        "ca-west-1": ["ca-west-1a", "ca-west-1b", "ca-west-1c"],
        "eu-central-1": ["eu-central-1a", "eu-central-1b", "eu-central-1c"],
        "eu-central-2": ["eu-central-2a", "eu-central-2b", "eu-central-2c"],
        "eu-west-1": ["eu-west-1a", "eu-west-1b", "eu-west-1c"],
        "eu-west-2": ["eu-west-2a", "eu-west-2b", "eu-west-2c"],
        "eu-west-3": ["eu-west-3a", "eu-west-3b", "eu-west-3c"],
        "eu-north-1": ["eu-north-1a", "eu-north-1b", "eu-north-1c"],
        "eu-south-1": ["eu-south-1a", "eu-south-1b", "eu-south-1c"],
        "eu-south-2": ["eu-south-2a", "eu-south-2b", "eu-south-2c"],
        "il-central-1": ["il-central-1a", "il-central-1b", "il-central-1c"],
        "me-south-1": ["me-south-1a", "me-south-1b", "me-south-1c"],
        "me-central-1": ["me-central-1a", "me-central-1b", "me-central-1c"],
        "sa-east-1": ["sa-east-1a", "sa-east-1b", "sa-east-1c"],
    },
    azure: {
        "eastus": ["1", "2", "3"],
        "eastus2": ["1", "2", "3"],
        "southcentralus": ["1", "2", "3"],
        "westus2": ["1", "2", "3"],
        "westus3": ["1", "2", "3"],
        "australiaeast": ["1", "2", "3"],
        "southeastasia": ["1", "2", "3"],
        "northeurope": ["1", "2", "3"],
        "uksouth": ["1", "2", "3"],
        "westeurope": ["1", "2", "3"],
        "brazilsouth": ["1", "2", "3"],
        "canadacentral": ["1", "2", "3"],
        "centralindia": ["1", "2", "3"],
        "francecentral": ["1", "2", "3"],
        "germanywestcentral": ["1", "2", "3"],
        "japaneast": ["1", "2", "3"],
        "koreacentral": ["1", "2", "3"],
        "norwayeast": ["1", "2", "3"],
        "southafricanorth": ["1", "2", "3"],
        "swedencentral": ["1", "2", "3"],
        "switzerlandnorth": ["1", "2", "3"],
        "uaenorth": ["1", "2", "3"],
        "qatarcentral": ["1", "2", "3"],
        "israelcentral": ["1", "2", "3"],
        "italynorth": ["1", "2", "3"],
        "polandcentral": ["1", "2", "3"],
        
    }
};


const providerRegionList: { [key: string]: string[] } = {
    gcp: Object.keys(providerRegionToZoneMap.gcp),
    aws: Object.keys(providerRegionToZoneMap.aws),
    azure: Object.keys(providerRegionToZoneMap.azure)
};


const workbenchTypeList = ["instance", "user managed notebook", "managed notebook"];

// --- Interfaces ---
interface CostLabelPair {
    id?: string;
    cost: string;
    label: string;
}

interface NotebookFormValues {
    name: string;
    application: string;
    cloudProvider: 'gcp' | 'aws' | 'azure';
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
    details: {
        region: string;
        zone: string;
        workbenchType: string;
    };
}
interface AddNotebookPageProps {
    isEdit: boolean;
}
interface NotebookFormContentProps {
    isEdit: boolean;
}

// --- Dynamic Field Updater Components ---
const NotebookCloudProviderUpdater: React.FC = () => {
    const { values, setFieldValue, initialValues } = useFormikContext<NotebookFormValues>();
    const selectedProvider = values.cloudProvider;

    useEffect(() => {
        if (values.cloudProvider === initialValues.cloudProvider && values.name !== "") { // Also check name to avoid firing on initial create
            return;
        }

        const availableRegions = providerRegionList[selectedProvider] || [];
        setFieldValue('details.region', availableRegions[0] || '');
        setFieldValue('details.zone', '');

    }, [selectedProvider, setFieldValue, initialValues.cloudProvider, values.name]);

    return null;
};

const ZoneUpdater: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<NotebookFormValues>();
    const selectedRegion = values.details.region;
    const selectedProvider = values.cloudProvider;

    useEffect(() => {
        const zonesForRegion = (providerRegionToZoneMap[selectedProvider]?.[selectedRegion]) || [];
        if (!zonesForRegion.includes(values.details.zone)) {
            setFieldValue('details.zone', '');
        }
    }, [selectedProvider, selectedRegion, values.details.zone, setFieldValue]);

    return null;
};

// --- Form Content Component ---
const NotebookFormContent: React.FC<NotebookFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id: notebookIdParams } = useParams<{ id:string }>();
    const [initialFormValues, setInitialFormValues] = useState<NotebookFormValues | null>(null);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    const [applications, setApplications] = useState<any[]>([]);
    const [applicationNamesList, setApplicationNamesList] = useState<string[]>([]);

    useEffect(() => {
    queryGraphql(`
        query {
            applications {
                id
                applicationName
                status
            }
        }
    `).then(data => {
        if (data.applications) {
            const activeApps = data.applications.filter((app: any) => app.status === 'active');
            setApplications(activeApps);
            const appNames = Array.from(new Set(activeApps.map((app: any) => app.applicationName)));
            setApplicationNamesList(appNames as string[]);
        }
    }).catch(err => console.error("Error fetching applications", err));
    }, []);

    useEffect(() => {
        if (isEdit && notebookIdParams) {
            setIsLoading(true);
            queryGraphql(`
                query GetNotebook($getNotebookId: String!) {
                  getNotebook(id: $getNotebookId) {
                    id
                    name
                    cloudProvider
                    application {
                      id
                      applicationName
                    }
                    costLabelPairs {
                      cost
                      label
                    }
                    details {
                      notebooks {
                        region
                        zone
                        workbenchType
                      }
                    }
                  }
                }
            `, { getNotebookId: notebookIdParams })
                .then(response => {
                    if (response.getNotebook) {
                        const nb = response.getNotebook;
                        const allPairs: { cost: string, label: string }[] = nb.costLabelPairs || [];
                        const costCenterPair = allPairs.find(p => p.cost === "cost-center");
                        const dynamicPairs = allPairs
                            .filter(p => p.cost !== "cost-center")
                            .map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

                        setInitialFormValues({
                            name: nb.name || "",
                            application: nb.application?.applicationName || "",
                            cloudProvider: nb.cloudProvider || "gcp",
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            details: {
                                region: nb.details?.notebooks?.region || "us-central1",
                                zone: nb.details?.notebooks?.zone || "",
                                workbenchType: nb.details?.notebooks?.workbenchType || "instance",
                            }
                        });
                    } else {
                        console.error("Notebook not found:", notebookIdParams, response.errors);
                        navigate("/aisolutions/notebooks");
                    }
                })
                .catch(err => {
                    console.error("Error fetching notebook:", err);
                    navigate("/aisolutions/notebooks");
                })
                .finally(() => setIsLoading(false));
        } else {
             // MODIFIED: Simplified initial values
            setInitialFormValues({
                name: "",
                application: "",
                cloudProvider: "gcp",
                costCenterValue: "",
                costLabelPairs: [],
                details: { region: "us-central1", zone: "", workbenchType: "instance" }
            });
        }
    }, [isEdit, notebookIdParams, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(100, "Max 100 characters"),
        application: Yup.string().required("Application is required"),
        cloudProvider: Yup.string().required("Cloud Provider is required"),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
        details: Yup.object().shape({
            region: Yup.string().required("Region is required"),
            zone: Yup.string().required("Zone is required"),
            workbenchType: Yup.string().required("Workbench Type is required"),
        }),
    });

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Notebook Data...</Typography>
            </Box>
        );
    }

    if (!initialFormValues) {
        return <Typography color="error" sx={{ p: 3 }}>Could not initialize form.</Typography>;
    }

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: { xs: 1, sm: "24px" }, bgcolor: "#fafafa" }}>
            <Formik
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setSubmitting(true);

                    const finalCostLabelPairs = [
                        { cost: "cost-center", label: values.costCenterValue },
                        ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
                    ];

                    const selectedApplication = applications.find(app => app.applicationName === values.application);

                    const apiPayload = {
                        name: values.name,
                        application: selectedApplication ? selectedApplication.id : undefined,
                        cloudProvider: values.cloudProvider,
                        costLabelPairs: finalCostLabelPairs,
                        details: {
                            notebooks: {
                                ...values.details
                            }
                        }
                    };

                    let mutationString;
                    let variables: { input: any };
                    let resultKey;

                    if (isEdit && notebookIdParams) {
                        mutationString = `mutation UpdateSolutionNotebooks($input: UpdateSolutionInput!) {
                          updateSolutionNotebooks(input: $input) {
                            id
                            name
                          }
                        }`;
                        variables = { input: { id: notebookIdParams, ...apiPayload } };
                        resultKey = 'updateSolutionNotebooks';
                    } else {
                        mutationString = `mutation CreateSolutionNotebooks($input: CreateSolutionInput!) {
                          createSolutionNotebooks(input: $input) {
                            id
                            name
                          }
                        }`;
                        variables = { input: apiPayload };
                        resultKey = 'createSolutionNotebooks';
                    }

                    try {
                        const responseData = await queryGraphql(mutationString, variables);
                        if (responseData[resultKey]) {
                            navigate("/aisolutions/notebooks");
                        } else {
                            const errorMsg = responseData.errors ? JSON.stringify(responseData.errors) : 'An unknown error occurred.';
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Notebook. ${errorMsg}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    }
                    setSubmitting(false);
                }}
            >
                {({ handleChange, handleBlur, values, errors, setFieldValue, touched, isValid, dirty }) => (
                    <Form id="notebook-static-form">
                        <NotebookCloudProviderUpdater />
                        <ZoneUpdater />
                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={3} mt={1}>
                            {isEdit ? "Edit the below details to edit Notebook" : "Fill the below details to add Notebook"}
                        </Typography>

                        {/* Cloud Provider Section */}
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider</Typography>
                             <Divider sx={{ mb: "24px" }} />
                             <Grid container spacing={3}>
                                 <Grid item xs={12}>
                                     <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                     <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
                                                <ErrorMessage name="cloudProvider" />
                                            </div>
                                     
                                 </Grid>
                             </Grid>
                        </Box>

                        {/* Details Section */}
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Name" />
                                    <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter notebook instance name" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Application" />
                                    <TextField
                                        select
                                        fullWidth
                                        name="application"
                                        value={values.application}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        variant="filled"
                                        hiddenLabel
                                        error={touched.application && Boolean(errors.application)}
                                        InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
                                    >
                                        {applicationNamesList.map(appName => (
                                            <MenuItem key={appName} value={appName}>
                                                {appName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="application" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Workbench Type" />
                                    <TextField select fullWidth name="details.workbenchType" value={values.details.workbenchType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.details?.workbenchType && Boolean(errors.details?.workbenchType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
                                        {workbenchTypeList.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.workbenchType" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Region" />
                                    <TextField select fullWidth name="details.region" value={values.details.region} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.cloudProvider} error={touched.details?.region && Boolean(errors.details?.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
                                        {(providerRegionList[values.cloudProvider] || []).map(region => <MenuItem key={region} value={region}>{region}</MenuItem>)}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.region" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Zone" />
                                    <TextField select fullWidth name="details.zone" value={values.details.zone} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.details.region} error={touched.details?.zone && Boolean(errors.details?.zone)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
                                        {((providerRegionToZoneMap[values.cloudProvider] || {})[values.details.region] || []).map(zone => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.zone" /></div>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Labels Section */}
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3} sx={{ mb: "16px" }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
                                    <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Value" />
                                    <TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
                                </Grid>
                            </Grid>
                            <FieldArray
                                name="costLabelPairs"
                                render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }} alignItems="center">
                                                <Grid item xs={12} sm={5}>
                                                    <CompulsoryFieldsLabel fieldName="Key" />
                                                    <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Value" />
                                                    <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: { sm: '20px' } }}>
                                                    <IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>
                                            + Add Key Value Pair
                                        </Button>
                                    </div>
                                )}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmationChecked}
                                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                                    name="confirmationCheckbox"
                                    sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                                />
                            }
                            label={
                                <Typography sx={{ color: 'black' }}>
                                    I confirm that the details provided are correct and I wish to proceed.
                                </Typography>
                            }
                            sx={{ mt: 2.5, display: 'flex' }}
                        />

                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button sx={{
                                color: "black",
                                borderColor: "black",
                                fontWeight: "bold",
                                textTransform: "none",
                                px: 4,
                            }}
                                variant="outlined"
                                size="large"
                                onClick={() => navigate("/aisolutions/notebooks")}>
                                Cancel
                            </Button>
                            <Button sx={{
                                backgroundColor: "black",
                                color: "white",
                                fontWeight: "bold",
                                "&:hover": {
                                    backgroundColor: "white",
                                    color: "black",
                                    borderColor: "black",
                                },
                                textTransform: "none",
                                px: 4,
                            }}
                                variant="contained"
                                size="large" type="submit" disabled={!(isValid && dirty && confirmationChecked) || isViewer}>
                                {isEdit ? 'Save Changes' : 'Add New Notebook'}
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

const AddNotebookPage: React.FC<AddNotebookPageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"aisolutions-notebooks"} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', overflowY: 'auto' }}>
                <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, sm: 3 }, mx: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                            <Link underline="hover" color="inherit" href="/aisolutions/notebooks" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/notebooks"); }}>Notebooks</Link>
                            <Typography color="text.primary">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
                        </Breadcrumbs>
                        <Box display="flex" alignItems="center" mt={1.5}>
                            <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/notebooks")} sx={{ mr: 1 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                            <Typography fontFamily="Gilroy-Bold" fontSize="22px">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ mx: '5vw' }}>
                        <NotebookFormContent isEdit={isEdit} />
                    </Box>
                </Container>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddNotebookPage;



// import React, { useState, useEffect } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Divider,
//     useTheme,
//     CircularProgress,
//     Checkbox,
//     FormControlLabel,
//     Breadcrumbs,
//     Link,
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, FieldArray, useFormikContext, getIn } from "formik";
// import * as Yup from "yup";
// import { useAuth } from "../../../helpers/AuthProvider";
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from "../../MiniBot";
// import MainDrawer from "../../MainDrawer";

// // --- Data for Dropdowns ---
// const gcpRegionToZoneMap: { [key: string]: string[] } = {
//     "us-central1": ["us-central1-a", "us-central1-b", "us-central1-c", "us-central1-f"],
//     "us-east1": ["us-east1-b", "us-east1-c", "us-east1-d"],
//     "us-east4": ["us-east4-a", "us-east4-b", "us-east4-c"],
//     "europe-west1": ["europe-west1-b", "europe-west1-c", "europe-west1-d"],
//     "europe-west4": ["europe-west4-a", "europe-west4-b", "europe-west4-c"],
//     "asia-south1": ["asia-south1-a", "asia-south1-b", "asia-south1-c"],
//     "asia-southeast1": ["asia-southeast1-a", "asia-southeast1-b", "asia-southeast1-c"],
//     "asia-northeast1": ["asia-northeast1-a", "asia-northeast1-b", "asia-northeast1-c"],
// };
// const regionList = Object.keys(gcpRegionToZoneMap);
// const workbenchTypeList = ["instance", "user managed notebook", "managed notebook"];

// // --- Interfaces ---
// interface CostLabelPair {
//     id?: string;
//     cost: string;
//     label: string;
// }
// // MODIFIED: Added application
// interface NotebookFormValues {
//     name: string;
//     application: string;
//     costCenterValue: string;
//     costLabelPairs: CostLabelPair[];
//     details: {
//         region: string;
//         zone: string;
//         workbenchType: string;
//     };
// }
// interface AddNotebookPageProps {
//     isEdit: boolean;
// }
// interface NotebookFormContentProps {
//     isEdit: boolean;
// }

// // --- Form Content Component ---
// const NotebookFormContent: React.FC<NotebookFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const { id: notebookIdParams } = useParams<{ id: string }>();
//     const [initialFormValues, setInitialFormValues] = useState<NotebookFormValues | null>(null);
//     const [isLoading, setIsLoading] = useState(isEdit);
//     const [confirmationChecked, setConfirmationChecked] = useState(false);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
//     const [applications, setApplications] = useState<any[]>([]);
//     const [applicationNamesList, setApplicationNamesList] = useState<string[]>([]);

//     useEffect(() => {
//     queryGraphql(`
//         query {
//             applications {
//                 id
//                 applicationName
//                 status
//             }
//         }
//     `).then(data => {
//         if (data.applications) {
//             // data.applications is now a flat array, so we can filter it directly.
//             const activeApps = data.applications.filter((app: any) => app.status === 'active');
//             setApplications(activeApps);
//             const appNames = Array.from(new Set(activeApps.map((app: any) => app.applicationName)));
//             setApplicationNamesList(appNames as string[]);
//         }
//     }).catch(err => console.error("Error fetching applications", err));
// }, []);

//     useEffect(() => {
//         if (isEdit && notebookIdParams) {
//             setIsLoading(true);
//             queryGraphql(`
//                 query GetNotebook($getNotebookId: String!) {
//                   getNotebook(id: $getNotebookId) {
//                     id
//                     name
//                     application {
//                       id
//                       applicationName
//                     }
//                     costLabelPairs {
//                       cost
//                       label
//                     }
//                     details {
//                       notebooks {
//                         region
//                         zone
//                         workbenchType
//                       }
//                     }
//                   }
//                 }
//             `, { getNotebookId: notebookIdParams })
//                 .then(response => {
//                     if (response.getNotebook) {
//                         const nb = response.getNotebook;
//                         const allPairs: { cost: string, label: string }[] = nb.costLabelPairs || [];
//                         const costCenterPair = allPairs.find(p => p.cost === "cost-center");
//                         const dynamicPairs = allPairs
//                             .filter(p => p.cost !== "cost-center")
//                             .map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

//                         setInitialFormValues({
//                             name: nb.name || "",
//                             application: nb.application?.applicationName || "",
//                             costCenterValue: costCenterPair ? costCenterPair.label : "",
//                             costLabelPairs: dynamicPairs,
//                             details: {
//                                 region: nb.details?.notebooks?.region || "us-central1",
//                                 zone: nb.details?.notebooks?.zone || "",
//                                 workbenchType: nb.details?.notebooks?.workbenchType || "instance",
//                             }
//                         });
//                     } else {
//                         console.error("Notebook not found:", notebookIdParams, response.errors);
//                         navigate("/aisolutions/notebooks");
//                     }
//                 })
//                 .catch(err => {
//                     console.error("Error fetching notebook:", err);
//                     navigate("/aisolutions/notebooks");
//                 })
//                 .finally(() => setIsLoading(false));
//         } else {
//             setInitialFormValues({
//                 name: "",
//                 application: "",
//                 costCenterValue: "",
//                 costLabelPairs: [],
//                 details: { region: "us-central1", zone: "", workbenchType: "instance" }
//             });
//         }
//     }, [isEdit, notebookIdParams, navigate]);

//     // MODIFIED: Added application validation
//     const validationSchema = Yup.object().shape({
//         name: Yup.string().required("This field is required").max(100, "Max 100 characters"),
//         application: Yup.string().required("Application is required"),
//         costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
//         costLabelPairs: Yup.array().of(Yup.object().shape({
//             cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
//             label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
//         })),
//         details: Yup.object().shape({
//             region: Yup.string().required("Region is required"),
//             zone: Yup.string().required("Zone is required"),
//             workbenchType: Yup.string().required("Workbench Type is required"),
//         }),
//     });

//     const ZoneUpdater: React.FC = () => {
//         const { values, setFieldValue } = useFormikContext<NotebookFormValues>();
//         const selectedRegion = values.details.region;
//         useEffect(() => {
//             const zonesForRegion = gcpRegionToZoneMap[selectedRegion] || [];
//             if (!zonesForRegion.includes(values.details.zone)) {
//                 setFieldValue('details.zone', '');
//             }
//         }, [selectedRegion, values.details.zone, setFieldValue]);
//         return null;
//     };

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading Notebook Data...</Typography>
//             </Box>
//         );
//     }

//     if (!initialFormValues) {
//         return <Typography color="error" sx={{ p: 3 }}>Could not initialize form.</Typography>;
//     }

//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 1, sm: "24px" }, bgcolor: "#fafafa" }}>
//             <Formik
//                 initialValues={initialFormValues}
//                 validationSchema={validationSchema}
//                 enableReinitialize={true}
//                 onSubmit={async (values, { setSubmitting, setStatus }) => {
//                     setSubmitting(true);

//                     const finalCostLabelPairs = [
//                         { cost: "cost-center", label: values.costCenterValue },
//                         ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
//                     ];

//                     const selectedApplication = applications.find(app => app.applicationName === values.application);

//                     // MODIFIED: Added application to API payload
//                     const apiPayload = {
//                         name: values.name,
//                         application: selectedApplication ? selectedApplication.id : undefined,
//                         costLabelPairs: finalCostLabelPairs,
//                         details: {
//                             notebooks: {
//                                 ...values.details
//                             }
//                         }
//                     };

//                     let mutationString;
//                     let variables: { input: any };
//                     let resultKey;

//                     if (isEdit && notebookIdParams) {
//                         mutationString = `mutation UpdateSolutionNotebooks($input: UpdateSolutionInput!) {
//                           updateSolutionNotebooks(input: $input) {
//                             id
//                             name
//                           }
//                         }`;
//                         variables = { input: { id: notebookIdParams, ...apiPayload } };
//                         resultKey = 'updateSolutionNotebooks';
//                     } else {
//                         mutationString = `mutation CreateSolutionNotebooks($input: CreateSolutionInput!) {
//                           createSolutionNotebooks(input: $input) {
//                             id
//                             name
//                           }
//                         }`;
//                         variables = { input: apiPayload };
//                         resultKey = 'createSolutionNotebooks';
//                     }

//                     try {
//                         const responseData = await queryGraphql(mutationString, variables);
//                         if (responseData) {
//                             navigate("/aisolutions/notebooks");
//                         } else {
//                             const errorMsg = responseData.errors ? JSON.stringify(responseData.errors) : 'An unknown error occurred.';
//                             setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Notebook. ${errorMsg}` });
//                         }
//                     } catch (error: any) {
//                         setStatus({ error: error.message || "An unexpected error occurred." });
//                     }
//                     setSubmitting(false);
//                 }}
//             >
//                 {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                     <Form id="notebook-static-form">
//                         <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={3} mt={1}>
//                             {isEdit ? "Edit the below details to edit Notebook" : "Fill the below details to add Notebook"}
//                         </Typography>

//                         {/* Details Section */}
//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Details</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <ZoneUpdater />
//                             <Grid container spacing={3}>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Name" />
//                                     <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter notebook instance name" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Application" />
//                                     <TextField
//                                         select
//                                         fullWidth
//                                         name="application"
//                                         value={values.application}
//                                         onChange={handleChange}
//                                         onBlur={handleBlur}
//                                         variant="filled"
//                                         hiddenLabel
//                                         error={touched.application && Boolean(errors.application)}
//                                         InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}
//                                     >
//                                         {applicationNamesList.map(appName => (
//                                             <MenuItem key={appName} value={appName}>
//                                                 {appName}
//                                             </MenuItem>
//                                         ))}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="application" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Workbench Type" />
//                                     <TextField select fullWidth name="details.workbenchType" value={values.details.workbenchType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.details?.workbenchType && Boolean(errors.details?.workbenchType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {workbenchTypeList.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.workbenchType" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Region" />
//                                     <TextField select fullWidth name="details.region" value={values.details.region} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.details?.region && Boolean(errors.details?.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {regionList.map(region => <MenuItem key={region} value={region}>{region}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.region" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Zone" />
//                                     <TextField select fullWidth name="details.zone" value={values.details.zone} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.details.region} error={touched.details?.zone && Boolean(errors.details?.zone)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {(gcpRegionToZoneMap[values.details.region] || []).map(zone => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.zone" /></div>
//                                 </Grid>
//                             </Grid>
//                         </Box>

//                         {/* Labels Section */}
//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <Grid container spacing={3} sx={{ mb: "16px" }}>
//                                 <Grid item xs={12} sm={6}>
//                                     <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
//                                     <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} />
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Value (Cost Center)" />
//                                     <TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
//                                 </Grid>
//                             </Grid>
//                             <FieldArray
//                                 name="costLabelPairs"
//                                 render={arrayHelpers => (
//                                     <div>
//                                         {values.costLabelPairs.map((pair, index) => (
//                                             <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
//                                                 <Grid item xs={12} sm={5}>
//                                                     <CompulsoryFieldsLabel fieldName="Key" />
//                                                     <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                     <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={6}>
//                                                     <CompulsoryFieldsLabel fieldName="Value" />
//                                                     <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                     <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: '20px' }}>
//                                                     <IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton>
//                                                 </Grid>
//                                             </Grid>
//                                         ))}
//                                         <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>
//                                             + Add Key Value Pair
//                                         </Button>
//                                     </div>
//                                 )}
//                             />
//                             <FormControlLabel
//                                 control={
//                                     <Checkbox
//                                         checked={confirmationChecked}
//                                         onChange={(e) => setConfirmationChecked(e.target.checked)}
//                                         name="confirmationCheckbox"
//                                         sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
//                                     />
//                                 }
//                                 label={
//                                     <Typography sx={{ color: 'black' }}>
//                                         I confirm that the details provided are correct and I wish to proceed.
//                                     </Typography>
//                                 }
//                                 sx={{ mt: 2.5, display: 'flex' }}
//                             />
//                         </Box>

//                         <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
//                             <Button sx={{
//                                 color: "black",
//                                 borderColor: "black",
//                                 fontWeight: "bold",
//                                 textTransform: "none",
//                                 px: 4,
//                             }}
//                                 variant="outlined"
//                                 size="large"
//                                 onClick={() => navigate("/aisolutions/notebooks")}>
//                                 Cancel
//                             </Button>
//                             <Button sx={{
//                                 backgroundColor: "black",
//                                 color: "white",
//                                 fontWeight: "bold",
//                                 "&:hover": {
//                                     backgroundColor: "white",
//                                     color: "black",
//                                     borderColor: "black",
//                                 },
//                                 textTransform: "none",
//                                 px: 4,
//                             }}
//                                 variant="contained"
//                                 size="large" type="submit" disabled={!(isValid && dirty && confirmationChecked) || isViewer}>
//                                 {isEdit ? 'Save Changes' : 'Add New Notebook'}
//                             </Button>
//                         </Box>
//                     </Form>
//                 )}
//             </Formik>
//         </Container>
//     );
// };

// const AddNotebookPage: React.FC<AddNotebookPageProps> = ({ isEdit }) => {
//     const navigate = useNavigate();

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"aisolutions-notebooks"} />
//             <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', overflowY: 'auto' }}>
//                 <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, sm: 3 }, mx: 1 }}>
//                     <Box sx={{ mb: 3 }}>
//                         <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                             <Link underline="hover" color="inherit" href="/aisolutions/notebooks" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/notebooks"); }}>Notebooks</Link>
//                             <Typography color="text.primary">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
//                         </Breadcrumbs>
//                         <Box display="flex" alignItems="center" mt={1.5}>
//                             <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/notebooks")} sx={{ mr: 1 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
//                             <Typography fontFamily="Gilroy-Bold" fontSize="22px">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
//                         </Box>
//                     </Box>
//                     <Box sx={{ mx: '5vw' }}>
//                         <NotebookFormContent isEdit={isEdit} />
//                     </Box>
//                 </Container>
//                 <MiniBot />
//             </Box>
//         </Box>
//     );
// };

// export default AddNotebookPage;



// // Filename: AddNotebookPage.tsx
// import React, { useState, useEffect } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Divider,
//     useTheme,
//     CircularProgress,
//     Checkbox,
//     FormControlLabel,
//     Breadcrumbs,
//     Link,
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, FieldArray, useFormikContext, getIn } from "formik";
// import * as Yup from "yup";
// import { useAuth } from "../../../helpers/AuthProvider";
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from "../../MiniBot";
// import MainDrawer from "../../MainDrawer";

// // --- Data for Dropdowns ---
// const gcpRegionToZoneMap: { [key: string]: string[] } = {
//     "us-central1": ["us-central1-a", "us-central1-b", "us-central1-c", "us-central1-f"],
//     "us-east1": ["us-east1-b", "us-east1-c", "us-east1-d"],
//     "us-east4": ["us-east4-a", "us-east4-b", "us-east4-c"],
//     "europe-west1": ["europe-west1-b", "europe-west1-c", "europe-west1-d"],
//     "europe-west4": ["europe-west4-a", "europe-west4-b", "europe-west4-c"],
//     "asia-south1": ["asia-south1-a", "asia-south1-b", "asia-south1-c"],
//     "asia-southeast1": ["asia-southeast1-a", "asia-southeast1-b", "asia-southeast1-c"],
//     "asia-northeast1": ["asia-northeast1-a", "asia-northeast1-b", "asia-northeast1-c"],
// };
// const regionList = Object.keys(gcpRegionToZoneMap);
// const workbenchTypeList = ["instance", "user managed notebook", "managed notebook"];

// // --- Interfaces ---
// interface CostLabelPair {
//     id?: string;
//     cost: string;
//     label: string;
// }
// // MODIFIED: Removed environment and networking
// interface NotebookFormValues {
//     name: string;
//     costCenterValue: string;
//     costLabelPairs: CostLabelPair[];
//     details: {
//         region: string;
//         zone: string;
//         workbenchType: string;
//     };
// }
// interface AddNotebookPageProps {
//     isEdit: boolean;
// }
// interface NotebookFormContentProps {
//     isEdit: boolean;
// }

// // --- Form Content Component ---
// const NotebookFormContent: React.FC<NotebookFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const { id: notebookIdParams } = useParams<{ id:string }>();
//     const [initialFormValues, setInitialFormValues] = useState<NotebookFormValues | null>(null);
//     const [isLoading, setIsLoading] = useState(isEdit);
//     const [confirmationChecked, setConfirmationChecked] = useState(false);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

//     useEffect(() => {
//         if (isEdit && notebookIdParams) {
//             setIsLoading(true);
//             queryGraphql(`
//                 query GetNotebook($getNotebookId: String!) {
//                   getNotebook(id: $getNotebookId) {
//                     id
//                     name
//                     costLabelPairs {
//                       cost
//                       label
//                     }
//                     details {
//                       notebooks {
//                         region
//                         zone
//                         workbenchType
//                       }
//                     }
//                   }
//                 }
//             `, { getNotebookId: notebookIdParams })
//             .then(response => {
//                 if (response.getNotebook) {
//                     const nb = response.getNotebook;
//                     const allPairs: { cost: string, label: string }[] = nb.costLabelPairs || [];
//                     const costCenterPair = allPairs.find(p => p.cost === "cost-center");
//                     const dynamicPairs = allPairs
//                         .filter(p => p.cost !== "cost-center")
//                         .map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

//                     // MODIFIED: Simplified initial values
//                     setInitialFormValues({
//                         name: nb.name || "",
//                         costCenterValue: costCenterPair ? costCenterPair.label : "",
//                         costLabelPairs: dynamicPairs,
//                         details: {
//                             region: nb.details?.notebooks?.region || "us-central1",
//                             zone: nb.details?.notebooks?.zone || "",
//                             workbenchType: nb.details?.notebooks?.workbenchType || "instance",
//                         }
//                     });
//                 } else {
//                     console.error("Notebook not found:", notebookIdParams, response.errors);
//                     navigate("/aisolutions/notebooks");
//                 }
//             })
//             .catch(err => {
//                 console.error("Error fetching notebook:", err);
//                 navigate("/aisolutions/notebooks");
//             })
//             .finally(() => setIsLoading(false));
//         } else {
//              // MODIFIED: Simplified initial values
//             setInitialFormValues({
//                 name: "", costCenterValue: "", costLabelPairs: [],
//                 details: { region: "us-central1", zone: "", workbenchType: "instance" }
//             });
//         }
//     }, [isEdit, notebookIdParams, navigate]);

//     // MODIFIED: Simplified validation schema
//     const validationSchema = Yup.object().shape({
//         name: Yup.string().required("This field is required").max(100, "Max 100 characters"),
//         costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
//         costLabelPairs: Yup.array().of(Yup.object().shape({
//             cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
//             label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
//         })),
//         details: Yup.object().shape({
//             region: Yup.string().required("Region is required"),
//             zone: Yup.string().required("Zone is required"),
//             workbenchType: Yup.string().required("Workbench Type is required"),
//         }),
//     });

//     const ZoneUpdater: React.FC = () => {
//         const { values, setFieldValue } = useFormikContext<NotebookFormValues>();
//         const selectedRegion = values.details.region;
//         useEffect(() => {
//             const zonesForRegion = gcpRegionToZoneMap[selectedRegion] || [];
//             if (!zonesForRegion.includes(values.details.zone)) {
//                 setFieldValue('details.zone', '');
//             }
//         }, [selectedRegion, values.details.zone, setFieldValue]);
//         return null;
//     };

//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}>
//                 <CircularProgress />
//                 <Typography sx={{ ml: 2 }}>Loading Notebook Data...</Typography>
//             </Box>
//         );
//     }

//     if (!initialFormValues) {
//         return <Typography color="error" sx={{p:3}}>Could not initialize form.</Typography>;
//     }

//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 1, sm: "24px" }, bgcolor: "#fafafa" }}>
//             <Formik
//                 initialValues={initialFormValues}
//                 validationSchema={validationSchema}
//                 enableReinitialize={true}
//                 onSubmit={async (values, { setSubmitting, setStatus }) => {
//                     setSubmitting(true);
                    
//                     const finalCostLabelPairs = [
//                         { cost: "cost-center", label: values.costCenterValue },
//                         ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))
//                     ];
                    
//                     // MODIFIED: Simplified API payload
//                     const apiPayload = {
//                         name: values.name,
//                         costLabelPairs: finalCostLabelPairs,
//                         details: {
//                             notebooks: {
//                                 ...values.details
//                             }
//                         }
//                     };

//                     let mutationString;
//                     let variables: { input: any };
//                     let resultKey;

//                     if (isEdit && notebookIdParams) {
//                         mutationString = `mutation UpdateSolutionNotebooks($input: UpdateSolutionInput!) {
//                           updateSolutionNotebooks(input: $input) {
//                             id
//                             name
//                           }
//                         }`;
//                         variables = { input: { id: notebookIdParams, ...apiPayload } };
//                         resultKey = 'updateSolutionNotebooks';
//                     } else {
//                         mutationString = `mutation CreateSolutionNotebooks($input: CreateSolutionInput!) {
//                           createSolutionNotebooks(input: $input) {
//                             id
//                             name
//                           }
//                         }`;
//                         variables = { input: apiPayload };
//                         resultKey = 'createSolutionNotebooks';
//                     }

//                     try {
//                         const responseData = await queryGraphql(mutationString, variables);
//                         // Assuming the result key might be in data property
//                         // const result = isEdit ? responseData.createSolutionNotebooks : responseData.updateSolutionNotebooks;
//                         //console.log("data",responseData);
//                         if (responseData) {
//                             navigate("/aisolutions/notebooks");
//                         } else {
//                             const errorMsg = responseData.errors ? JSON.stringify(responseData.errors) : 'An unknown error occurred.';
//                             setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Notebook. ${errorMsg}` });
//                         }
//                     } catch (error: any) {
//                         setStatus({ error: error.message || "An unexpected error occurred." });
//                     }
//                     setSubmitting(false);
//                 }}
//             >
//                 {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                     <Form id="notebook-static-form">
//                         <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={3} mt={1}>
//                             {isEdit ? "Edit the below details to edit Notebook" : "Fill the below details to add Notebook"}
//                         </Typography>
                        
//                         {/* Details Section */}
//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Details</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <ZoneUpdater />
//                             <Grid container spacing={3}>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Name" />
//                                     <TextField fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter notebook instance name" variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Workbench Type" />
//                                     <TextField select fullWidth name="details.workbenchType" value={values.details.workbenchType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.details?.workbenchType && Boolean(errors.details?.workbenchType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {workbenchTypeList.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.workbenchType" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Region" />
//                                     <TextField select fullWidth name="details.region" value={values.details.region} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.details?.region && Boolean(errors.details?.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {regionList.map(region => <MenuItem key={region} value={region}>{region}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.region" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Zone" />
//                                     <TextField select fullWidth name="details.zone" value={values.details.zone} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel disabled={!values.details.region} error={touched.details?.zone && Boolean(errors.details?.zone)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} >
//                                         {(gcpRegionToZoneMap[values.details.region] || []).map(zone => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="details.zone" /></div>
//                                 </Grid>
//                             </Grid>
//                         </Box>

//                          {/* Labels Section */}
//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <Grid container spacing={3} sx={{ mb: "16px" }}>
//                                 <Grid item xs={12} sm={6}>
//                                     <Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography>
//                                     <TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} />
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Value (Cost Center)" />
//                                     <TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div>
//                                 </Grid>
//                             </Grid>
//                             <FieldArray
//                                 name="costLabelPairs"
//                                 render={arrayHelpers => (
//                                     <div>
//                                         {values.costLabelPairs.map((pair, index) => (
//                                             <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
//                                                 <Grid item xs={12} sm={5}>
//                                                     <CompulsoryFieldsLabel fieldName="Key" />
//                                                     <TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                     <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={6}>
//                                                     <CompulsoryFieldsLabel fieldName="Value" />
//                                                     <TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                     <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div>
//                                                 </Grid>
//                                                 <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: '20px' }}>
//                                                     <IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton>
//                                                 </Grid>
//                                             </Grid>
//                                         ))}
//                                         <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>
//                                             + Add Key Value Pair
//                                         </Button>
//                                     </div>
//                                 )}
//                             />
//                             <FormControlLabel
//                             control={
//                                 <Checkbox
//                                     checked={confirmationChecked}
//                                     onChange={(e) => setConfirmationChecked(e.target.checked)}
//                                     name="confirmationCheckbox"
//                                     sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
//                                 />
//                             }
//                             label={
//                                 <Typography sx={{ color: 'black' }}>
//                                     I confirm that the details provided are correct and I wish to proceed.
//                                 </Typography>
//                             }
//                             sx={{ mt: 2.5, display: 'flex' }}
//                         />
//                         </Box>

//                         {/* REMOVED: Environment and Networking Sections */}
                        
                        

//                     {/* {status && status.error && (<Typography color="error" sx={{ my: 2, textAlign: 'center' }}>{status.error}</Typography>)} */}
//                         <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
//                             <Button sx={{
//                                                     color: "black",
//                                                     borderColor: "black",
//                                                     fontWeight: "bold",
//                                                     textTransform: "none",
//                                                     px: 4,
//                                                 }}
//                                                 variant="outlined"
//                                                 size="large" 
//                                                 onClick={() => navigate("/aisolutions/notebooks")}>
//                                 Cancel
//                             </Button>
//                             <Button sx={{
//                                                     backgroundColor: "black",
//                                                     color: "white",
//                                                     fontWeight: "bold",
//                                                     "&:hover": {
//                                                         backgroundColor: "white",
//                                                         color: "black",
//                                                         borderColor: "black",
//                                                     },
//                                                     textTransform: "none",
//                                                     px: 4,
//                                                 }}
//                                                 variant="contained"
//                                                 size="large" type="submit" disabled={ !(isValid && dirty && confirmationChecked) || isViewer }>
//                                 {isEdit ? 'Save Changes' : 'Add New Notebook'}
//                             </Button>
//                         </Box>
//                     </Form>
//                 )}
//             </Formik>
//         </Container>
//     );
// };

// const AddNotebookPage: React.FC<AddNotebookPageProps> = ({ isEdit }) => {
//     const navigate = useNavigate();

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"aisolutions-notebooks"} />
//             <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', overflowY: 'auto' }}>
//                 <Container maxWidth={false}  sx={{py: 2, px: {xs: 1, sm: 3}, mx:1}}>
//                     <Box sx={{ mb: 3 }}>
//                         <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                             <Link underline="hover" color="inherit" href="/aisolutions/notebooks" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/notebooks"); }}>Notebooks</Link>
//                             <Typography color="text.primary">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
//                         </Breadcrumbs>
//                         <Box display="flex" alignItems="center" mt={1.5}>
//                             <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/notebooks")} sx={{ mr: 1 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
//                             <Typography fontFamily="Gilroy-Bold" fontSize="22px">{isEdit ? "Edit Notebook" : "Add New Notebook"}</Typography>
//                         </Box>
//                     </Box>
//                     <Box sx={{mx:'5vw'}}>
//                         <NotebookFormContent isEdit={isEdit} />
//                     </Box>
//                 </Container>
//                  <MiniBot />
//             </Box>
//         </Box>
//     );
// };

// export default AddNotebookPage;
